/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useParams } from 'react-router'
import { FormattedMessage } from 'react-intl'

import { EuiPageHeader, EuiSpacer } from '@elastic/eui'

import type { ProjectID } from '@modules/project-admin-api/v1/types'
import { useGetProjectQuery } from '@modules/project-admin-lib/hooks/get'
import type { ProjectType } from '@modules/ui-types/projects'
import ApiConsole from '@modules/api-console/ApiConsole'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import { useProxyMutation } from '@modules/project-admin-lib/hooks/proxy'

import { projectApiConsoleCrumbs } from './breadcrumbs'

import type { ComponentPropsWithoutRef } from 'react'

type Props = {
  projectType: ProjectType
}

const AdminProjectApiConsole: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams<{ projectId: ProjectID }>()

  const projectQuery = useGetProjectQuery(projectType, projectId)
  const proxyMutation = useProxyMutation(projectType, projectId)

  const { data: project } = projectQuery

  if (!project) {
    return null
  }

  const onRequest: ComponentPropsWithoutRef<typeof ApiConsole>['onRequest'] = proxyMutation.mutate

  return (
    <div>
      <Breadcrumbs
        breadcrumbs={projectApiConsoleCrumbs({
          projectType,
          projectId,
          projectName: project.name,
        })}
      />
      <EuiPageHeader
        bottomBorder={true}
        pageTitle={
          <FormattedMessage id='projectApiConsole.pageTitle' defaultMessage='API Console' />
        }
      />

      <EuiSpacer />

      <ApiConsole
        onRequest={onRequest}
        isLoading={proxyMutation.isLoading}
        response={proxyMutation.data}
      />
    </div>
  )
}

export default AdminProjectApiConsole
